import { PhoneNumberFormat, PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber'

/*
 In order to ensure that we store all numbers consistently and validate them in a unified way,
 we format the phone numbers according to the international E164 standard.
 Also this format guarantees that each number is unique, and has a min and max length.
 For more information, please refer to:
 https://hellogetsafe.atlassian.net/wiki/spaces/GETSAFE/pages/1127153668/MFT-1040+Investigate+questions+around+phone+number+verification
*/
export const formatPhoneNumber = (number: string): string => {
  const phoneUtil = PhoneNumberUtil.getInstance()
  try {
    const parsed = phoneUtil.parse(number)
    return phoneUtil.format(parsed, PhoneNumberFormat.E164)
  } catch {
    return number
  }
}

export const getCountryCode = (number: string): string | undefined => {
  const phoneUtil = PhoneNumberUtil.getInstance()
  try {
    // Note: "00" at the beginning of a number won't be converted to "+" because "00" isn't an international code for all countries
    // https://github.com/google/libphonenumber/blob/master/FAQ.md#why-wasnt-00-parsed-the-same-as-
    const parsed = phoneUtil.parse(number)
    return phoneUtil.getRegionCodeForNumber(parsed)
  } catch {
    return undefined
  }
}

export const isValidNumber = (number: string) => {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance()
    const parsed = phoneUtil.parse(number)

    // we should exclude landline phone numbers and treat them as invalid ones
    // some landline codes can be the same as country codes, and it results in a wrong number format
    // e.g. there is a German landline code 0491 so the phone numbers like +49 4915201234567 are considered as valid ones, while we shouldn't allow it
    const phoneNumberType = phoneUtil.getNumberType(parsed)

    // PhoneNumberType.FIXED_LINE means a landline number format
    // all phone number types can be found here: https://github.com/google/libphonenumber/blob/bbd48d57125f0d409d657c6fb582aa5a2c91f851/cpp/src/phonenumbers/phonenumberutil.h#L94C17-L123
    return phoneUtil.isValidNumber(parsed) && phoneNumberType !== PhoneNumberType.FIXED_LINE
  } catch {
    return false
  }
}
